import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BarChart3, Loader2, ArrowUpRight, FolderKanban, ListChecks, Files, TrendingUp, Trophy, GitBranch } from "lucide-react";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";

const ease = [0.16, 1, 0.3, 1];
const STATUS_LABEL = { nuevo: "Nuevo", en_progreso: "En progreso", en_revision: "En revisión", completado: "Completado", archivado: "Archivado" };
const fmtMoney = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(n || 0);

export default function StudioDashboard() {
  const { studioId } = useParams();
  const [meta, setMeta] = useState(null);
  const [sum, setSum] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/catalog/studio/${studioId}`).then((r) => setMeta(r.data)).catch(() => {}),
      api.get(`/workspace/studio/${studioId}/summary`).then((r) => setSum(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [studioId]);

  const p = sum?.premium;
  const isCrm = p?.kind === "crm";
  const kpis = isCrm ? [
    { label: "Leads totales", value: p.leads_total }, { label: "Leads abiertos", value: p.open_leads },
    { label: "Pipeline", value: fmtMoney(p.pipeline_value) }, { label: "Tasa de cierre", value: `${p.win_rate}%` },
  ] : p ? [
    { label: "Proyectos", value: p.projects }, { label: "Activos", value: p.active },
    { label: "Aprobación pendiente", value: p.pending_approval }, { label: "Avance promedio", value: `${p.avg_progress}%` },
  ] : (sum ? [
    { label: "Registros totales", value: sum.total }, { label: "Abiertos", value: sum.open },
    { label: "Completados", value: sum.done }, { label: "Prioridad alta", value: sum.high },
  ] : []);
  const maxStatus = sum ? Math.max(1, ...Object.values(sum.by_status)) : 1;
  const maxPipe = isCrm ? Math.max(1, ...p.pipeline.map((s) => s.value)) : 1;

  return (
    <OSLayout title={meta?.studio || "Studio"}>
      <div className="space-y-8" data-testid="studio-dashboard">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
          <div className="overline text-navy/40 mb-2 flex items-center gap-2"><BarChart3 className="w-3.5 h-3.5" /> {meta?.suite} · Panel del Studio</div>
          <h2 className="font-editorial text-4xl md:text-5xl tracking-tight" data-testid="studio-title">{meta?.studio || studioId}</h2>
          <p className="mt-2 text-navy/50 max-w-2xl">Visión analítica agregada de todas las Apps de este Studio.</p>
        </motion.div>

        {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> : <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="studio-kpis">
            {kpis.map((k) => (<div key={k.label} className="card-surface p-5"><div className="overline text-navy/40">{k.label}</div><div className="mt-3 text-2xl font-medium tracking-tight">{k.value}</div></div>))}
          </div>

          {p && !isCrm && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="studio-premium">
              <div className="card-surface p-5" data-testid="premium-tasks">
                <div className="overline text-navy/40 flex items-center gap-1.5"><ListChecks className="w-3.5 h-3.5" /> Tareas</div>
                <div className="mt-3 text-2xl font-medium tracking-tight">{p.tasks_total}</div>
                <div className="mt-1 text-xs text-navy/50">{p.tasks_done} completadas · {p.tasks_open} abiertas</div>
              </div>
              <div className="card-surface p-5" data-testid="premium-income">
                <div className="overline text-navy/40 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Ingresos</div>
                <div className="mt-3 text-2xl font-medium tracking-tight text-emerald-600">{fmtMoney(p.income)}</div>
                <div className="mt-1 text-xs text-navy/50">Gastos {fmtMoney(p.expenses)}</div>
              </div>
              <div className="card-surface p-5" data-testid="premium-profit">
                <div className="overline text-navy/40 flex items-center gap-1.5"><FolderKanban className="w-3.5 h-3.5" /> Rentabilidad</div>
                <div className={`mt-3 text-2xl font-medium tracking-tight ${p.profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{fmtMoney(p.profit)}</div>
                <div className="mt-1 text-xs text-navy/50">Ingresos − Gastos</div>
              </div>
              <div className="card-surface p-5" data-testid="premium-files">
                <div className="overline text-navy/40 flex items-center gap-1.5"><Files className="w-3.5 h-3.5" /> Archivos</div>
                <div className="mt-3 text-2xl font-medium tracking-tight">{p.files}</div>
                <div className="mt-1 text-xs text-navy/50">Documentos del studio</div>
              </div>
            </div>
          )}

          {isCrm && (
            <div className="grid gap-6 lg:grid-cols-3" data-testid="crm-premium">
              <div className="card-surface p-6 lg:col-span-2" data-testid="crm-pipeline">
                <div className="overline text-navy/40 mb-4 flex items-center gap-1.5"><GitBranch className="w-3.5 h-3.5" /> Pipeline por etapa (monto)</div>
                <div className="space-y-3">
                  {p.pipeline.map((s) => (
                    <div key={s.stage} className="flex items-center gap-3" data-testid={`crm-stage-${s.stage}`}>
                      <div className="w-28 text-sm text-navy/60 shrink-0">{s.label} <span className="text-navy/35">· {s.count}</span></div>
                      <div className="flex-1 h-2.5 rounded-full bg-navy/10 overflow-hidden">
                        <div className={`h-full ${s.stage === "won" ? "bg-emerald-500" : s.stage === "lost" ? "bg-rose-400" : "bg-gradient-violet"}`} style={{ width: `${(s.value / maxPipe) * 100}%` }} />
                      </div>
                      <div className="w-28 text-right text-sm text-navy/60 tabular-nums">{fmtMoney(s.value)}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-4">
                <div className="card-surface p-5" data-testid="crm-won">
                  <div className="overline text-navy/40 flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" /> Ganado</div>
                  <div className="mt-3 text-2xl font-medium tracking-tight text-emerald-600">{fmtMoney(p.won_value)}</div>
                  <div className="mt-1 text-xs text-navy/50">Tasa de cierre {p.win_rate}%</div>
                </div>
                <div className="card-surface p-5" data-testid="crm-pipeline-value">
                  <div className="overline text-navy/40 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Pipeline abierto</div>
                  <div className="mt-3 text-2xl font-medium tracking-tight">{fmtMoney(p.pipeline_value)}</div>
                  <div className="mt-1 text-xs text-navy/50">{p.open_leads} leads abiertos</div>
                </div>
                <Link to="/os/crm" className="btn btn-primary w-full justify-center gap-1.5 text-sm" data-testid="crm-open-board">Abrir CRM <ArrowUpRight className="w-3.5 h-3.5" /></Link>
              </div>
            </div>
          )}

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="card-surface p-6">
              <div className="overline text-navy/40 mb-4">Distribución por estado</div>
              <div className="space-y-3">
                {sum && Object.entries(sum.by_status).map(([st, n]) => (
                  <div key={st} className="flex items-center gap-3" data-testid={`studio-status-${st}`}>
                    <div className="w-28 text-sm text-navy/60 shrink-0">{STATUS_LABEL[st]}</div>
                    <div className="flex-1 h-2.5 rounded-full bg-navy/10 overflow-hidden"><div className="h-full bg-gradient-violet" style={{ width: `${(n / maxStatus) * 100}%` }} /></div>
                    <div className="w-8 text-right text-sm text-navy/50">{n}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card-surface p-6">
              <div className="overline text-navy/40 mb-4">Apps del Studio</div>
              <div className="space-y-1 max-h-80 overflow-y-auto">
                {sum?.apps?.map((a) => (
                  <Link key={a.id} to={`/os/app/${a.id}`} data-testid={`studio-app-${a.id}`} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-navy/5 transition-colors group">
                    <span className="text-sm truncate flex items-center gap-1.5">{a.name} <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-60" /></span>
                    <span className={`badge ${a.count > 0 ? "badge-violet" : "badge-muted"}`}>{a.count}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </>}
      </div>
    </OSLayout>
  );
}
